<template>
  <Card
    padding-class="tw-p-0"
    class="tw-overflow-hidden tw-border tw-border-secondary-100 tw-@container"
  >
    <Disclosure>
      <template #activator="{ toggle, isActive, ariaExpanded, ariaControls }">
        <div
          class="tw-flex tw-flex-col tw-space-y-4 tw-p-4 tw-transition-colors @xl:tw-flex-row @xl:tw-items-center @xl:tw-space-x-5 @xl:tw-space-y-0"
          :class="[
            isActive
              ? 'tw-border-b tw-border-secondary-100 tw-bg-secondary-50'
              : '',
          ]"
        >
          <ContentDeliveryIcon
            class="tw-hidden tw-h-12 tw-w-12 tw-shrink-0 @xl:tw-block"
          />
          <div class="tw-grow">
            <div class="tw-font-semibold tw-text-secondary-500">
              Let’s find a few suitable mentors for you. Powered by GPT-4
            </div>
            <div class="tw-mt-1 tw-text-sm tw-text-secondary-400">
              Get matches in a few easy steps. We’ll help ensure the mentors are
              relevant to you, and having upcoming availabilities.
            </div>
          </div>
          <div class="tw-shrink-0">
            <IconButton
              v-if="isActive"
              @click="toggle"
              :aria-expanded="ariaExpanded"
              :aria-controls="ariaControls"
              color="outline-light-purple"
              :id="closeButtonId"
            >
              <BaseIcon name="e-remove-1" />
            </IconButton>
            <BaseButton
              v-else
              @click="toggle"
              :aria-expanded="ariaExpanded"
              :aria-controls="ariaControls"
              color="outline-light-purple"
            >
              <LightningIcon class="tw-mr-2 tw-h-4 tw-w-4" />
              Get matched
            </BaseButton>
          </div>
        </div>
      </template>
      <template #content>
        <form @submit.prevent="handleSubmitForm" class="tw-p-6">
          <FormInput
            v-model="matchingForm.challenge"
            as="textarea"
            rows="4"
            tooltip="Write a couple of sentences describing the core challenge you're facing."
            :min-characters="50"
            :max-characters="2000"
            :is-error="Boolean(matchingForm.errors.challenge)"
            :errors="matchingForm.errors.challenge"
            @input="matchingForm.clearErrors('challenge')"
            @click="handleInputClick"
            :readonly="!isUserLoggedIn"
          >
            <template #label>What’s your main pain/challenge?</template>
          </FormInput>
          <FormInput
            v-model="matchingForm.outcome"
            as="textarea"
            rows="4"
            tooltip="Paint a picture of what a successful session would look like to you."
            :min-characters="100"
            :max-characters="500"
            :is-error="Boolean(matchingForm.errors.outcome)"
            :errors="matchingForm.errors.outcome"
            @input="matchingForm.clearErrors('outcome')"
            @click="handleInputClick"
            :readonly="!isUserLoggedIn"
            class="tw-mt-4"
          >
            <template #label>What’s the desired outcome?</template>
          </FormInput>
          <div
            class="tw-mt-4 tw-flex tw-items-center tw-justify-center tw-text-sm"
          >
            <div v-if="matchingForm.processing" class="tw-text-neutral-500">
              <LoadingCircle size="16" class="tw-mr-2 tw-inline-block" />
              Matching...
            </div>
            <div
              v-else-if="matchingForm.wasSuccessful"
              class="tw-text-success-500"
            >
              {{ SUCCESS_MESSAGE }}
            </div>
          </div>

          <div class="tw-text-right">
            <BaseButton
              type="submit"
              color="light-purple"
              :is-disabled="matchingForm.processing"
            >
              <LightningIcon class="tw-mr-2 tw-h-4 tw-w-4" />
              Get matches
            </BaseButton>
          </div>
        </form>
      </template>
    </Disclosure>
  </Card>
</template>

<script setup>
import Card from '@/shared/components/Card.vue'
import BaseButton from '@/shared/components/BaseButton.vue'
import LightningIcon from '@/assets/images/icons/lightning.svg'
import ContentDeliveryIcon from '@/assets/images/colored-icons/content-delivery.svg'
import Disclosure from '@/shared/components/Disclosure.vue'
import BaseIcon from '@/shared/components/BaseIcon.vue'
import IconButton from '@/shared/components/IconButton.vue'
import FormInput from '@/shared/components/forms/FormInput.vue'
import LoadingCircle from '@/shared/components/LoadingCircle.vue'
import { useMatchingForm } from '@/modules/growthmentor/matches/composables/useMatchingForm.ts'
import { UNLOCK_FULL_ACCESS_MODAL_NAME } from '@/shared/data/globalVariables.js'
import { usePermissionSystem } from '@/shared/composables/usePermissionSystem.js'
import { useModals } from '@/shared/composables/useModals.js'
import { getUniqueId } from '@/shared/utils/uniqueId.js'

const { isUserLoggedIn } = usePermissionSystem()
const { openModal } = useModals()
const { matchingForm, SUCCESS_MESSAGE, submitForm } = useMatchingForm()

const handleSubmitForm = () => {
  submitForm({
    url: '/matches',
    method: 'POST',
    onSuccess: () => {
      closeDisclosure()
    },
  })
}

const handleInputClick = () => {
  if (isUserLoggedIn.value) return
  openModal(UNLOCK_FULL_ACCESS_MODAL_NAME)
}

// Hack to close the disclosure when the form is submitted
const closeButtonId = `close-button-${getUniqueId()}`
const closeDisclosure = () => {
  const closeButton = document.getElementById(closeButtonId)
  closeButton.click()
}
</script>
